import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button as ButtonB } from 'react-bootstrap';
import { useProject } from '@contexts/Project.context';
import { LuPalette, LuFileDown, LuSlidersHorizontal } from 'react-icons/lu';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import VectorSource from 'ol/source/Vector';
import styled from 'styled-components';

import { useAuth } from '@contexts/User.context';
import { getDetections } from '@utils/api';
import { toObjectUrl, downloadFile } from '@utils/files';
import { titleCaseReplace } from '@utils/helpers';

import {
	getPointRadius,
	noOverlapOffset,
	setupDetectionDetails,
	filterDetections,
	getFeatures,
	getColorSchemeStyle,
	deleteLayerByCustomId,
} from '@utils/map/helpers';

import InfoTooltip from '@components/infoTooltip/InfoTooltip';
import LayerSettings from '../../sidebars/sidebarElements/LayerSettings';

import LoadingDots from '@components/loading/LoadingDots';
import NoResult from '../../sidebars/sidebarElements/NoAnalyzesResult';
import DetailsLayer from './Details';
import DetectionsPalette from './components/DetectionsPalette';
import FilterDetections from './components/FilterDetections';

const Header = styled.div`
	display: grid;
	grid-template-columns: max-content auto;
	column-gap: 16px;
`;

const IconWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
`;

const Wrapper = styled.div`
	padding-left: 16px;

	* {
		list-style: none;
	}
`;

const Button = styled(ButtonB)`
	padding: 4px;

	transition: all 0.2s ease-in-out;
	opacity: 0.6;
	pointer-events: auto;
	display: grid;

	& > span {
		display: grid;
	}

	&:hover,
	&:focus,
	:where(li):hover > &,
	:where(div):hover > & {
		opacity: 1;
		pointer-events: auto;
	}

	&:disabled {
		opacity: 0.3;
		pointer-events: none;
	}
`;

const Subtitle = styled.div`
	font-weight: 300;
	color: ${p => p.theme.colors.textAccent};
`;

const LoadingWrapper = styled(LoadingDots)`
	color: ${p => p.theme.colors.textColor};
	font-weight: 400;
	font-size: 0.9rem;
	margin: 0;
	opacity: 0.7;
	font-style: italic;
`;

const IconButton = ({ title, icon, ...props }) => {
	return (
		<Button variant="dark" {...props}>
			<InfoTooltip
				tooltipContainerId="tooltip-sidebar-root"
				placement="top"
				data-tooltip-offset={20}
				content={title}
				showIcon={false}>
				{icon}
			</InfoTooltip>
		</Button>
	);
};

const Layers = ({ detectionLayers, features }) => {
	// Show nothing if detection layers are not ready or no detections have been created by setup
	if (!detectionLayers || Object.values(features).every(arr => arr.length == 0)) return null;

	const entries = Object.entries(detectionLayers);
	return (
		<>
			{entries.map(([name, layer]) => {
				if (!features[name]?.length) return null;
				return (
					<li key={name}>
						<LayerSettings
							id={`${name}Layer`}
							layer={layer}
							label={layer?.get('name')}
							defaultState={layer?.getVisible()}
							onCheck={() => layer?.setVisible(true)}
							onUncheck={() => layer?.setVisible(false)}
						/>
					</li>
				);
			})}
			<DetailsLayer />
		</>
	);
};

// Local Style Constants
const STROKE_WIDTH = 2;

/** Local Style For Detection Layers */
const getLayerStyle = colorScheme => ({
	'circle-fill-color': getColorSchemeStyle(colorScheme),
	'circle-radius': getPointRadius(),

	'stroke-color': getColorSchemeStyle(colorScheme),
	'stroke-width': STROKE_WIDTH,
	'stroke-offset': noOverlapOffset(STROKE_WIDTH),
	'fill-color': getColorSchemeStyle(colorScheme, 0.2),
});

const DetectionResults = ({}) => {
	const { roleAdmin } = useAuth();

	const layerConfigs = [
		{ name: 'center', zIndex: 13, visible: true },
		{ name: 'outline', zIndex: 12, visible: false },
		roleAdmin && { name: 'bounding_box', zIndex: 14, visible: false },
	];

	const {
		project,
		pickedTask,
		colorOptions,
		detections,
		features,
		detectionsFilter,
		mapObject,
		colorScheme,
		defaultFormat,
		setActiveSidebar,
		dispatch,
	} = useProject() ?? {};

	const [setupLoading, setSetupLoading] = useState(true);
	const [detectionLayers, setDetectionLayers] = useState(null);
	const { uuid: projectUuid, title } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	// Fetch detections when a model is picked
	const {
		data,
		isLoading: geojsonLoading,
		error,
	} = useQuery({
		queryKey: ['fetchDetections', projectUuid, modelUuid],
		queryFn: () => getDetections(projectUuid, modelUuid),
		enabled: !!projectUuid && !!modelUuid,
		refetchInterval: 60_000 * 5, // 5min
		refetchIntervalInBackground: true,
	});

	// Creates Layers
	useEffect(() => {
		const layers = {};
		for (const { name, zIndex, visible } of layerConfigs) {
			const layerInstance = new WebGLVectorLayer({
				name: titleCaseReplace(name),
				zIndex,
				visible,
				source: new VectorSource({}),
				properties: { customLayerId: `${name}Layer` },
				style: {},
			});

			mapObject.addLayer(layerInstance);
			layers[name] = layerInstance;
		}
		setDetectionLayers(layers);

		return () => {
			for (const { name } of layerConfigs) deleteLayerByCustomId(mapObject, `${name}Layer`);
			setDetectionLayers(null);
		};
	}, []);

	// Sets up detections, color scheme and model classes for the entire app
	useEffect(() => {
		setSetupLoading(true);
		if (error) {
			setSetupLoading(false);
			return console.error('Error fetching detections: ', error);
		}

		const details = setupDetectionDetails(data?.features ?? [], colorOptions);
		dispatch({ type: 'setDetections', payload: details.detections });
		dispatch({ type: 'setPickedModelClasses', payload: details.modelClasses });
		dispatch({ type: 'setColorScheme', payload: details.colorScheme });
		setSetupLoading(false);

		return () => {
			dispatch({ type: 'setDetections', payload: [] });
			dispatch({ type: 'setPickedModelClasses', payload: null });
		};
	}, [data, error]);

	// Sets style and source for each layer.
	useEffect(() => {
		if (!detectionLayers) return;

		const activeFilter = detectionsFilter?.isDrafting
			? detectionsFilter?.draft
			: detectionsFilter?.applied;

		const style = getLayerStyle(colorScheme);
		const filteredDetections = filterDetections(detections, colorScheme, activeFilter);
		const filteredFeatures = getFeatures(filteredDetections);
		dispatch({ type: 'setFeatures', payload: filteredFeatures });

		for (const [name, layer] of Object.entries(detectionLayers)) {
			layer.setStyle(style);
			layer.setSource(
				new VectorSource({ features: filteredFeatures[name], format: defaultFormat })
			);
		}

		return () => {
			dispatch({ type: 'setFeatures', payload: {} });
		};
	}, [detectionLayers, detections, colorScheme, detectionsFilter]);

	const geojsonHasDetections = data?.features?.length > 0;

	const geojsonUrl = useMemo(() => toObjectUrl(data), [data]);
	const downloadGeojson = () =>
		downloadFile({ url: geojsonUrl, title, fileExtension: 'geojson' });

	return (
		<>
			<div>
				<Header>
					<Subtitle className="mb-1">Detection Results</Subtitle>
					<IconWrapper>
						<IconButton
							title="Filter"
							icon={<LuSlidersHorizontal />}
							disabled={setupLoading || !geojsonHasDetections}
							onClick={() =>
								setActiveSidebar({
									sidebarId: 'filterDetections',
									parentId: 'layerView',
								})
							}
						/>
						<IconButton
							title="Palette"
							icon={<LuPalette />}
							disabled={setupLoading || !geojsonHasDetections}
							onClick={() =>
								setActiveSidebar({
									sidebarId: 'detectionsPalette',
									parentId: 'layerView',
								})
							}
						/>

						<IconButton
							title="Export GeoJSON"
							icon={<LuFileDown />}
							disabled={geojsonLoading || !geojsonHasDetections}
							onClick={downloadGeojson}
						/>
					</IconWrapper>
				</Header>
				<Wrapper>
					{setupLoading || geojsonLoading ? (
						<LoadingWrapper>Loading</LoadingWrapper>
					) : !geojsonHasDetections ? (
						<NoResult text="No objects found" />
					) : (
						<Layers detectionLayers={detectionLayers} features={features} />
					)}
				</Wrapper>
				<FilterDetections />
				<DetectionsPalette />
			</div>
			<hr />
		</>
	);
};

export default DetectionResults;
